<script setup lang="ts">
// import dynamic components
import {
  Contact,
  UiPrivacyPolicy,
  UiSalesConditions,
  UiCustomerService,
  UiEUSupport,
} from '#components'

const { renderVueForStaticContent } = useRuntimeConfig().public
const { getComponentForStaticPageSlug } = useStaticPages()

const route = useRoute()
const localePath = useLocalePath()
const { addSuffix } = useMeta()
const slug = route.path.split('/').pop()

const { t, locale } = useI18n()

const pagesRepository = createPagesRepository()

const {
  data: page,
  error,
  status,
} = await useLazyAsyncData(() => pagesRepository.getBySlug(slug))

watch(
  error,
  async () => {
    if (error.value) {
      const { data, error: slugNotFound } = await useAsyncData(() =>
        pagesRepository.translateSlug(slug)
      )

      if (data.value && data.value.urlSlug) {
        localePath({
          name: 'slug',
          params: { slug: data.value.urlSlug },
        })
      }

      if (slugNotFound.value) {
        throw createError({
          statusCode: 404,
          statusMessage: t('error.pageNotFound'),
          fatal: true,
        })
      }
    }
  },
  { immediate: true }
)

const { defaultOgImage } = useOgImage()

useSeoMeta({
  title: addSuffix(page?.value?.title) ?? t('pages.index.meta.title'),
  description: t('pages.index.meta.description'),
  ogImage: page?.value?.backgroundImage?.url ?? defaultOgImage,
})

const staticPageComponent = computed(() => {
  switch (getComponentForStaticPageSlug(slug)) {
    case Contact.__name:
      return Contact
    case UiPrivacyPolicy.__name:
      return UiPrivacyPolicy
    case UiSalesConditions.__name:
      return UiSalesConditions
    case UiCustomerService.__name:
      return UiCustomerService
    case UiEUSupport.__name:
      return UiEUSupport
    default:
      return Contact
  }
})
</script>

<template>
  <main>
    <div v-if="status === 'pending'">
      <UiSkeletonBanner />
      <UiSkeletonContentBlock />
      <UiSkeletonContentBlock />
    </div>
    <div v-else-if="status === 'success'">
      <UiBanner :title="page?.title ?? ''" :slug="page?.title ?? undefined" />
      <component :is="staticPageComponent" v-if="renderVueForStaticContent" />
      <div v-else v-html="page?.content" />
      <ContactSalesmen
        v-if="getComponentForStaticPageSlug(slug) === Contact.__name"
      />
    </div>
  </main>
</template>
